import clsx from 'clsx';

export interface SegmentedToggleOption {
  id: string;
  label: string;
}

interface Props {
  value: string;
  options: SegmentedToggleOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'pill';
}

export function SegmentedToggle(props: Props) {
  const {
    value,
    options,
    onChange,
    disabled = false,
    className,
    size = 'medium',
    variant = 'default',
  } = props;

  const handleClick = (optionId: string) => {
    if (!disabled) {
      onChange(optionId);
    }
  };

  const isPill = variant === 'pill';

  return (
    <div
      className={clsx(
        'inline-flex',
        isPill
          ? 'dark:bg-ondark-bg-1 rounded-full bg-white p-1.5'
          : clsx(
              'rounded-md border',
              'border-onlight-line-2 dark:border-ondark-line-2',
              'bg-onlight-bg-2 dark:bg-ondark-bg-2',
              'gap-1 p-1'
            ),
        className
      )}
      role="group"
    >
      {options.map((option) => {
        const isSelected = value === option.id;
        return (
          <button
            key={option.id}
            onClick={() => handleClick(option.id)}
            disabled={disabled}
            className={clsx(
              'font-medium transition-all duration-200 ease-in-out',
              isPill
                ? clsx(
                    'rounded-full px-2.5 py-1 text-sm',
                    isSelected
                      ? 'dark:bg-ondark-line-3 dark:text-ondark-text-1 bg-gray-200 text-gray-900'
                      : 'dark:text-ondark-text-2 text-gray-500'
                  )
                : clsx(
                    'rounded-md',
                    size === 'small' && 'px-3 py-1 text-xs',
                    size === 'medium' && 'px-4 py-2 text-sm',
                    size === 'large' && 'px-6 py-3 text-base',
                    isSelected
                      ? 'bg-primary-600 dark:bg-primary-500 border-primary-600 dark:border-primary-500 border text-white'
                      : clsx(
                          'text-onlight-text-2 dark:text-ondark-text-2 border border-transparent bg-transparent',
                          !disabled &&
                            'hover:text-onlight-text-1 dark:hover:text-ondark-text-1'
                        )
                  ),
              disabled && 'cursor-not-allowed opacity-80',
              !disabled && !isPill && 'active:scale-95'
            )}
            aria-pressed={isSelected}
            title={option.label}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedToggle;
